import React, { useRef, useEffect, useState } from 'react';
import './Footer.css';
import SociosSection from './SociosSection';
import { FaMapMarkerAlt, FaEnvelope, FaFacebookF, FaInstagram, FaTiktok, FaWhatsapp } from 'react-icons/fa';

function useOnScreen(ref) {
  const [isIntersecting, setIntersecting] = useState(false);
  useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => setIntersecting(entry.isIntersecting),
      { threshold: 0.3 }
    );
    const currentRef = ref.current;
    if (currentRef) observer.observe(currentRef);
    return () => { if (currentRef) observer.unobserve(currentRef); };
  }, [ref]);
  return isIntersecting;
}

function Counter({ to }) {
  const [count, setCount] = useState(1);
  const ref = useRef();
  const visible = useOnScreen(ref);
  useEffect(() => {
    if (!visible) return;
    let start = 1;
    let end = to;
    let duration = 1200;
    let startTime = null;
    function animate(ts) {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      setCount(Math.floor(progress * (end - start) + start));
      if (progress < 1) requestAnimationFrame(animate);
      else setCount(end);
    }
    requestAnimationFrame(animate);
  }, [visible, to]);
  return <span ref={ref} className="contador-empresarios">{count.toLocaleString()}</span>;
}

export default function Footer() {
  const socialRef = useRef();
  const [showAnim, setShowAnim] = useState(false);

  useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => setShowAnim(entry.isIntersecting),
      { threshold: 0.3 }
    );
    const currentSocialRef = socialRef.current;
    if (currentSocialRef) observer.observe(currentSocialRef);
    return () => { if (currentSocialRef) observer.unobserve(currentSocialRef); };
  }, []);

  return (
    <footer className="footer">
      <div className="footer-content">
        <p className="footer-question">IMPULSALTE CON CONEXIONES QUE SUMAN</p>
        <h2 className="footer-title">
          <span className="contador-empresarios">+</span><Counter to={500} /> EMPRESARIOS<br />
          CON CONEXIÓN DE VALOR REAL<br />
          EN SUS NEGOCIOS.
        </h2>
        <SociosSection />
        <div className="footer-logo-container">
          <a href="/">
            <img src="/images/RecomiendaT-logo.png" alt="Logo RecomiendaT" className="footer-logo" />
          </a>
          <div className="footer-slider-textos">
            <FooterSliderTextos />
          </div>
        </div>
        <div className="footer-social-links" ref={socialRef}>
          <a
            href="https://www.facebook.com/RecomiendaT/"
            target="_blank"
            rel="noopener noreferrer"
            className={`social-icon-link${showAnim ? ' slide-in-left' : ''}`}
            aria-label="Facebook"
            title="Facebook"
            style={{ gridRow: 1, gridColumn: 1 }}
          >
            <FaFacebookF className="social-icon facebook" />
          </a>
          <a
            href="https://www.instagram.com/recomiendatemx/"
            target="_blank"
            rel="noopener noreferrer"
            className={`social-icon-link${showAnim ? ' slide-in-right' : ''}`}
            aria-label="Instagram RecomiendateMX"
            title="Instagram"
            style={{ gridRow: 1, gridColumn: 2 }}
          >
            <FaInstagram className="social-icon instagram" />
          </a>
          <span className="icon-house-wrapper" style={{ gridRow: 2, gridColumn: 1 }}>
            <a
              href="https://www.instagram.com/recomiendateconceptstore/"
              target="_blank"
              rel="noopener noreferrer"
              className={`social-icon-link${showAnim ? ' slide-in-left' : ''}`}
              aria-label="Instagram ConceptStore"
              title="Instagram Concept Store"
            >
              <span className="icon-house-roof"></span>
              <FaInstagram className="social-icon instagram" />
            </a>
          </span>
          <a
            href="https://www.tiktok.com/@recomiendate.red/"
            target="_blank"
            rel="noopener noreferrer"
            className={`social-icon-link${showAnim ? ' slide-in-right' : ''}`}
            aria-label="TikTok"
            title="TikTok"
            style={{ gridRow: 2, gridColumn: 2 }}
          >
            <FaTiktok className="social-icon tiktok" />
          </a>
        </div>
        <p className="footer-contact" id="footer-contacto">CONTÁCTANOS</p>
        <div className="footer-contact-info">
          <div className="contact-item">
            <a href="https://maps.app.goo.gl/tRukCk2KxVvKqY4q7" target="_blank" rel="noopener noreferrer" className="contact-link" aria-label="Ubicación" title="Ver Mapa">
              <span className="contact-icon"><FaMapMarkerAlt /></span>
              Abedul 161, Balcones de Santa María, 58090 Morelia, Mich.
            </a>
          </div>
          <div className="contact-item">
            <a href="mailto:hola@recomiendate.com" className="contact-link" title="Correo">
              <span className="contact-icon"><FaEnvelope /></span>
              hola@recomiendate.com
            </a>
          </div>
          <div className="contact-item">
            <a href="http://wa.me/524434036600" target="_blank" rel="noopener noreferrer" className="contact-link" aria-label="WhatsApp" title="WhatsApp">
              <span className="contact-icon"><FaWhatsapp /></span>
              443 403 6600
            </a>
          </div>
        </div>
      </div>
      <div className="footer-legal">
        <a href="/aviso-legal" className="footer-legal-link">AVISO LEGAL</a>
        <span className="footer-legal-separator"> </span>
        <a href="/politica-privacidad" className="footer-legal-link">POLÍTICA DE PRIVACIDAD</a>
      </div>
      <div className="footer-copyright">
        <p>© 2025 Recomiendate.com</p>
      </div>
    </footer>
  );
}

function FooterSliderTextos() {
  const textos = [
    'RODÉATE DE GIGANTES',
    'ÚNETE A RECOMIENDA-T',
    'CLUB EMPRESARIAL',
  ];
  const [index, setIndex] = React.useState(0);
  const [animating, setAnimating] = React.useState(false);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setAnimating(true);
      setTimeout(() => {
        setIndex(i => (i + 1) % textos.length);
        setAnimating(false);
      }, 600); // duración de la animación
    }, 3500);
    return () => clearInterval(interval);
  }, [index, textos.length]);

  return (
    <span className="footer-slider-cube">
      <span className={`footer-slider-texto slider${animating ? ' animating' : ''}`}>{textos[index]}</span>
      {animating ? (
        <span className={`footer-slider-texto slider-next animating`}>{textos[(index + 1) % textos.length]}</span>
      ) : null}
    </span>
  );
} 